import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  FormControl,
  FormLabel,
  VStack,
  FormErrorMessage,
  Textarea,
  Text,
  Spinner,
  Box,
  Flex,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { useFaqContext } from "@root/context/faq-context";
import { CRUD_TYPE_ENUM } from "@root/constants";
import Time from "react-datepicker/dist/time";

const AddFaqModal: React.FC<AddFaqModalProps> = ({
  isOpen,
  onClose,
  selectedFaq,
  handleClosing,
  action,
}) => {
  const { t } = useTranslation("common");

  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [existingDocs, setExistingDocs] = useState<any[]>([]);

  const { addFaq, addFaqLoading, doUpdateFaq, fetchFaqs } = useFaqContext();

  const isFormValid = question.trim().length > 0 && answer.trim().length > 0;

  const handleSubmit = async () => {
    setIsSubmitted(true);

    const formData = new FormData();
    formData.append("question", question.trim());
    formData.append("answer", answer.trim());

    attachments.forEach((file) => {
      formData.append("documents", file);
    });

    try {
      if (action === CRUD_TYPE_ENUM.UPDATE) {
        await doUpdateFaq(formData, selectedFaq?.id);
      } else {
        await addFaq(formData);
      }

      await fetchFaqs({
        limit: 10,
        offset: 0,
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleResetAndClose = () => {
    if (typeof handleClosing === "function") {
      handleClosing();
    }
    setQuestion("");
    setAnswer("");
    setAttachments([]);
    setExistingDocs([]); 
    setIsSubmitted(false);
    onClose();
  };

  useEffect(() => {
    if (selectedFaq) {
      setQuestion(selectedFaq.question || "");
      setAnswer(selectedFaq.answer || "");
      setExistingDocs(selectedFaq.documents || []);
      setAttachments([]);
    } else {
      setQuestion("");
      setAnswer("");
      setExistingDocs([]);
      setAttachments([]);
    }
  }, [selectedFaq]);

  // Close modal when loading ends (after successful submission)
  useEffect(() => {
    if (!addFaqLoading && isSubmitted) {
      handleResetAndClose();
    }
  }, [addFaqLoading, isSubmitted]);

  return (
    <Modal isOpen={isOpen} onClose={handleResetAndClose} size="xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p={6} bg="#FFFFFF">
        <ModalHeader fontSize="24px" fontWeight="semibold" color="#2F2F34">
          {action === CRUD_TYPE_ENUM.ADD
            ? t("faq.addNewFaq")
            : action === CRUD_TYPE_ENUM.UPDATE
            ? t("faq.updateFaq")
            : ""}
        </ModalHeader>

        <ModalCloseButton />
        <ModalBody>
          <Text mb={4} color="#8B919E" fontSize="14px" fontWeight="400">
            {action === CRUD_TYPE_ENUM.ADD
              ? t("faq.description")
              : action === CRUD_TYPE_ENUM.UPDATE
              ? t("faq.updateDescription")
              : ""}
          </Text>
          <VStack spacing={6}>
            <FormControl isInvalid={isSubmitted && !question.trim()}>
              <FormLabel fontSize="16px" fontWeight="500">
                {t("faq.questionLabel")}
              </FormLabel>
              <Input
                color="#8B919E"
                fontSize="14px"
                fontWeight="400"
                placeholder={t("faq.questionPlaceholder")}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
              />
              <FormErrorMessage>{t("faq.questionError")}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={isSubmitted && !answer.trim()}>
              <FormLabel fontSize="16px" fontWeight="500">
                {t("faq.answerLabel")}
              </FormLabel>
              <Textarea
                color="#8B919E"
                fontSize="14px"
                fontWeight="400"
                placeholder={t("faq.answerPlaceholder")}
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows={5}
              />
              <FormErrorMessage>{t("faq.answerError")}</FormErrorMessage>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="16px" fontWeight="500">
                {t("faq.attachmentsLabel") || "Attachments"}
              </FormLabel>
              <Flex
                justifyContent="center"
                alignItems="center"
                border="1px dashed #A0AEC0"
                borderRadius="10px"
                height="60px"
                position="relative"
                bg="#F9FAFB"
                _hover={{ bg: "#F1F2F6" }}
                cursor="pointer"
              >
                {/* Hidden file input */}
                <Input
                  id="file-upload"
                  type="file"
                  multiple
                  accept=".pdf,.hwp,.doc,.docx,.xls,.xlsx,.png"
                  opacity={0}
                  position="absolute"
                  top={0}
                  left={0}
                  width="100%"
                  height="100%"
                  cursor="pointer"
                  onChange={(e) =>
                    setAttachments(
                      e.target.files ? Array.from(e.target.files) : []
                    )
                  }
                />

                {/* Centered text display */}
                <Text fontSize="14px" color="#4A5568" pointerEvents="none">
                  {attachments?.length
                    ? `${attachments.length} file(s) selected`
                    : existingDocs?.length
                    ? `${existingDocs.length} existing file(s)`
                    : "Choose Files"}
                </Text>
              </Flex>

              <Text mt={2} fontSize="12px" color="#8B919E">
                {t("faq.supportedFormat")}
              </Text>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={4}>
          <Button
            onClick={handleResetAndClose}
            bg="#FFFFFF"
            color="#2F2F34"
            w="87px"
            maxW="fit-content"
            fontSize="14px"
            fontWeight="500"
            border="1px solid"
            borderColor="#FFFFFF"
          >
            {t("faq.cancelButton")}
          </Button>
          <Button
            bg="#1819B4"
            fontSize="14px"
            borderRadius="8px"
            width="119px"
            maxW="fit-content"
            h="44px"
            color="#FFFFFF"
            onClick={handleSubmit}
            _hover={{ bg: "#0f108c" }}
            isDisabled={!isFormValid}
            isLoading={addFaqLoading}
          >
            {addFaqLoading ? (
              <Spinner size="sm" />
            ) : action === CRUD_TYPE_ENUM.ADD ? (
              t("faq.submitButton")
            ) : CRUD_TYPE_ENUM.UPDATE ? (
              t("faq.updateButton")
            ) : (
              ""
            )}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddFaqModal;
